import { Head, Link, router, usePage } from "@inertiajs/react"
import { useState, useEffect, useRef } from "react"
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import type { Account, DashboardListing, SocialPost, Video, SharedProps } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import {
  ListingCardWithVideos,
  ListingRowWithVideos,
} from "@/components/dashboard/listings/listing-card-with-videos"
import ViewToggle from "@/components/dashboard/view-toggle"
import IntroOfferBanner from "@/components/dashboard/intro-offer-banner"
import { useViewPreference } from "@/hooks/use-view-preference"
import { useCreditBalance } from "@/hooks/use-credit-balance"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import PlatformIcon from "@/components/dashboard/platform-icon"
import { PLATFORM_LABELS, STATUS_STYLES } from "@/lib/social-post-constants"

type ListingCounts = { draft: number; active: number; archived: number; total: number }
type PostCounts = { pending: number; scheduled: number; uploading: number; published: number; failed: number; total: number }
type Analytics = { views: number; likes: number; comments: number; shares: number; engagement_rate: number; post_count: number }
type SparklinePoint = { date: string; views: number }
type AttentionItems = { failed_posts: number; draft_videos: number; pending_imports: number }
type ContentTab = "listings" | "videos" | "posts"

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function timeAgo(dateStr: string | Date): string {
  const now = new Date()
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

export default function DashboardHome({
  account,
  recent_listings,
  search_listings,
  listing_counts,
  post_counts,
  analytics,
  sparkline,
  recent_videos,
  recent_posts,
  attention,
  q,
}: {
  account: Account
  recent_listings?: DashboardListing[]
  search_listings?: DashboardListing[]
  listing_counts?: ListingCounts
  post_counts?: PostCounts
  analytics?: Analytics
  sparkline?: SparklinePoint[]
  recent_videos?: Video[]
  recent_posts?: SocialPost[]
  attention?: AttentionItems
  q?: string | null
}) {
  const { currentUser } = usePage<SharedProps>().props
  const firstName = currentUser?.name?.split(" ")[0]

  const [query, setQuery] = useState(q || "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSearching = !!q

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const trimmed = query.trim()
      if (trimmed === (q || "")) return

      if (trimmed) {
        router.get("/dashboard", { q: trimmed }, { preserveState: true, replace: true })
      } else {
        router.get("/dashboard", {}, { preserveState: true, replace: true })
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleClear() {
    setQuery("")
    router.get("/dashboard", {}, { preserveState: true, replace: true })
  }

  return (
    <DashboardLayout>
      <Head title="Dashboard | QuiteRight" />

      {/* Welcome header with avatars and new post CTA */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* User avatar (circle) */}
          <Avatar className="size-14 shrink-0 border-2 border-white shadow-sm">
            <AvatarImage src={currentUser?.avatar_url || undefined} />
            <AvatarFallback className="bg-stone/40 text-xs font-medium">
              {currentUser?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?"}
            </AvatarFallback>
          </Avatar>

          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
              Welcome back{firstName ? `, ${firstName}` : ""}
            </h1>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-sm text-warm-grey">
              {account.logo_url && (
                <img src={account.logo_url} alt="" className="size-5 shrink-0 rounded object-contain" />
              )}
              <span>{account.name}</span>
              {account.website_url && (
                <a
                  href={account.website_url.startsWith("http") ? account.website_url : `https://${account.website_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-red transition-colors hover:text-deep-red"
                >
                  {account.website_url.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          </div>

        </div>
      </div>

      <IntroOfferBanner />

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
          <svg className="size-5 text-warm-grey/60" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search listings..."
          className="h-12 rounded-xl border-stone/50 bg-white pl-12 pr-11 text-base shadow-sm placeholder:text-warm-grey/40 focus-visible:border-red/30 focus-visible:ring-red/10"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-3 flex items-center text-warm-grey/50 transition-colors hover:text-charcoal"
          >
            <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {isSearching ? (
        <SearchResults q={q!} listings={search_listings || []} />
      ) : (
        <DashboardView
          recentListings={recent_listings || []}
          recentVideos={recent_videos || []}
          recentPosts={recent_posts || []}
          listingCounts={listing_counts}
          postCounts={post_counts}
          analytics={analytics}
          sparkline={sparkline || []}
          attention={attention}
        />
      )}
    </DashboardLayout>
  )
}

/* ── Dashboard View ─────────────────────────────────────────────── */

function DashboardView({
  recentListings,
  recentVideos,
  recentPosts,
  listingCounts,
  postCounts,
  analytics,
  sparkline,
  attention,
}: {
  recentListings: DashboardListing[]
  recentVideos: Video[]
  recentPosts: SocialPost[]
  listingCounts?: ListingCounts
  postCounts?: PostCounts
  analytics?: Analytics
  sparkline: SparklinePoint[]
  attention?: AttentionItems
}) {
  const hasAttention = attention && (attention.failed_posts > 0 || attention.draft_videos > 0 || attention.pending_imports > 0)

  return (
    <>
      {/* Hero metrics */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total views" value={formatCompact(analytics?.views ?? 0)} />
        <MetricCard label="Posts published" value={formatCompact(postCounts?.published ?? 0)} />
        <MetricCard label="Active listings" value={formatCompact(listingCounts?.active ?? 0)} />
        <MetricCard label="Engagement rate" value={`${analytics?.engagement_rate ?? 0}%`} />
      </div>

      {/* Attention banner */}
      {hasAttention && <AttentionBanner attention={attention!} />}

      {/* Two-column layout */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <EngagementSparkline data={sparkline} hasData={(analytics?.post_count ?? 0) > 0} />
          <RecentContent
            listings={recentListings}
            videos={recentVideos}
            posts={recentPosts}
          />
        </div>

        {/* Right column — first two cards match the sparkline height */}
        <div className="flex min-w-0 flex-col gap-6">
          <QuickActionsCard />
          <CreditsSummary />
        </div>
      </div>
    </>
  )
}

/* ── Metric Card ────────────────────────────────────────────────── */

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-stone/30 bg-white p-5 transition-colors hover:bg-cream/40">
      <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-charcoal">
        {value}
      </p>
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-charcoal/[0.03] transition-transform duration-300 group-hover:scale-150" />
    </div>
  )
}

/* ── Attention Banner ───────────────────────────────────────────── */

function AttentionBanner({ attention }: { attention: AttentionItems }) {
  const items: { label: string; href: string; count: number }[] = []

  if (attention.failed_posts > 0) {
    items.push({ label: `${attention.failed_posts} failed post${attention.failed_posts !== 1 ? "s" : ""}`, href: "/dashboard/posts?status=failed", count: attention.failed_posts })
  }
  if (attention.draft_videos > 0) {
    items.push({ label: `${attention.draft_videos} draft video${attention.draft_videos !== 1 ? "s" : ""}`, href: "/dashboard/videos?status=draft", count: attention.draft_videos })
  }
  if (attention.pending_imports > 0) {
    items.push({ label: `${attention.pending_imports} import${attention.pending_imports !== 1 ? "s" : ""} in progress`, href: "/dashboard/listings", count: attention.pending_imports })
  }

  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-stone/40 bg-white px-5 py-3.5">
      <span className="size-2 shrink-0 rounded-full bg-red/70" />
      {items.map((item, i) => (
        <Link
          key={item.href}
          href={item.href}
          className="text-sm text-charcoal transition-colors hover:text-red"
        >
          {item.label}
          {i < items.length - 1 && <span className="ml-5 text-stone">·</span>}
        </Link>
      ))}
    </div>
  )
}

/* ── Engagement Sparkline ───────────────────────────────────────── */

function EngagementSparkline({ data, hasData }: { data: SparklinePoint[]; hasData: boolean }) {
  return (
    <div className="rounded-xl border border-stone/30 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-charcoal">
          Views — last 30 days
        </h2>
        <Link
          href="/dashboard/analytics"
          className="text-xs font-medium text-warm-grey transition-colors hover:text-red"
        >
          View analytics
        </Link>
      </div>

      {hasData ? (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C41E2A" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#C41E2A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => {
                  const date = new Date(d)
                  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                }}
                tick={{ fontSize: 10, fill: "#8E8E93" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <Tooltip content={<SparklineTooltip />} />
              <Area
                type="monotone"
                dataKey="views"
                stroke="#C41E2A"
                fill="url(#sparkGradient)"
                strokeWidth={1.5}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center">
          <p className="text-sm text-warm-grey">No data yet. Publish posts to see engagement here.</p>
        </div>
      )}
    </div>
  )
}

function SparklineTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  const formattedDate = label
    ? new Date(String(label)).toLocaleDateString("en-GB", { day: "numeric", month: "long" })
    : ""

  return (
    <div className="rounded-lg border border-stone/30 bg-white px-3 py-2 shadow-md">
      <p className="text-[11px] text-warm-grey">{formattedDate}</p>
      <p className="text-sm font-semibold tabular-nums text-charcoal">
        {formatCompact(payload[0].value)} views
      </p>
    </div>
  )
}

/* ── Recent Content (Tabbed) ────────────────────────────────────── */

function RecentContent({
  listings,
  videos,
  posts,
}: {
  listings: DashboardListing[]
  videos: Video[]
  posts: SocialPost[]
}) {
  const [tab, setTab] = useState<ContentTab>("listings")

  return (
    <div className="rounded-xl border border-stone/30 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-1">
          {(["listings", "videos", "posts"] as ContentTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                tab === t
                  ? "bg-red text-white"
                  : "text-warm-grey hover:bg-stone/20 hover:text-charcoal"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <Link
          href={`/dashboard/${tab}`}
          className="text-xs font-medium text-warm-grey transition-colors hover:text-red"
        >
          View all
        </Link>
      </div>

      {tab === "listings" && <ListingsTab listings={listings} />}
      {tab === "videos" && <VideosTab videos={videos} />}
      {tab === "posts" && <PostsTab posts={posts} />}
    </div>
  )
}

function ListingsTab({ listings }: { listings: DashboardListing[] }) {
  if (listings.length === 0) {
    return <EmptyTab message="No listings yet." href="/dashboard/listings/new" cta="Import a listing" />
  }

  return (
    <div className="space-y-1">
      {listings.map((listing) => (
        <Link
          key={listing.id}
          href={`/dashboard/listings/${listing.slug}`}
          className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-stone/10"
        >
          {listing.image_urls[0] ? (
            <img src={listing.image_urls[0]} alt="" className="size-10 shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-stone/20">
              <svg className="size-5 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25a2.25 2.25 0 0 0-2.25-2.25H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
              </svg>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-charcoal">{listing.address}</p>
            <p className="text-xs text-warm-grey">
              {listing.price && <span>{listing.price} · </span>}
              {listing.bedrooms && <span>{listing.bedrooms} bed · </span>}
              {listing.image_count} photos
            </p>
          </div>
          <span className="shrink-0 text-[11px] text-warm-grey/50">
            {timeAgo(listing.created_at)}
          </span>
        </Link>
      ))}
    </div>
  )
}

function VideosTab({ videos }: { videos: Video[] }) {
  if (videos.length === 0) {
    return <EmptyTab message="No videos yet." href="/dashboard/listings" cta="Create from a listing" />
  }

  return (
    <div className="space-y-1">
      {videos.map((video) => (
        <Link
          key={video.id}
          href={`/dashboard/videos/${video.id}`}
          className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-stone/10"
        >
          {video.hero_image_url ? (
            <img src={video.hero_image_url} alt="" className="size-10 shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-stone/20">
              <svg className="size-5 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-charcoal">{video.name}</p>
            <p className="text-xs text-warm-grey">
              {video.listing_address}
            </p>
          </div>
          <Badge variant="outline" className="shrink-0 text-[10px]">
            {video.status}
          </Badge>
        </Link>
      ))}
    </div>
  )
}

function PostsTab({ posts }: { posts: SocialPost[] }) {
  if (posts.length === 0) {
    return <EmptyTab message="No posts yet." href="/dashboard/posts" cta="Create a post" />
  }

  return (
    <div className="space-y-1">
      {posts.map((post) => {
        const statusStyle = STATUS_STYLES[post.status]
        return (
          <div
            key={post.id}
            className="flex items-center gap-3 rounded-lg px-2 py-2.5"
          >
            <PlatformIcon platform={post.platform} className="size-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-charcoal">
                {post.caption || post.listing_address || "Untitled post"}
              </p>
              <p className="text-xs text-warm-grey">
                {PLATFORM_LABELS[post.platform]}
                {post.published_at && <span> · {timeAgo(post.published_at)}</span>}
              </p>
            </div>
            {statusStyle && (
              <Badge variant="outline" className={`shrink-0 text-[10px] ${statusStyle.className}`}>
                {statusStyle.label}
              </Badge>
            )}
          </div>
        )
      })}
    </div>
  )
}

function EmptyTab({ message, href, cta }: { message: string; href: string; cta: string }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <p className="text-sm text-warm-grey">{message}</p>
      <Link
        href={href}
        className="mt-2 text-sm font-medium text-red transition-colors hover:text-deep-red"
      >
        {cta}
      </Link>
    </div>
  )
}

/* ── Quick Actions Card ─────────────────────────────────────────── */

function QuickActionsCard() {
  return (
    <div className="flex flex-1 flex-col rounded-xl border border-stone/30 bg-white p-5">
      <h2 className="mb-4 font-display text-base font-semibold text-charcoal">
        Quick actions
      </h2>

      <div className="space-y-1">
        <Link
          href="/dashboard/listings/new"
          className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-stone/10"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red">
            <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-charcoal">Import listing</p>
            <p className="text-[11px] text-warm-grey">Paste a property URL</p>
          </div>
        </Link>

        <Link
          href="/dashboard/videos"
          className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-stone/10"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red">
            <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-charcoal">Your videos</p>
            <p className="text-[11px] text-warm-grey">Manage and render</p>
          </div>
        </Link>

        <Link
          href="/dashboard/posts"
          className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-stone/10"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red">
            <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-charcoal">Publish a post</p>
            <p className="text-[11px] text-warm-grey">Share to social media</p>
          </div>
        </Link>
      </div>

      {/* Explore links */}
      <div className="mt-4 border-t border-stone/20 pt-4">
        <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-wider text-warm-grey/60">Explore</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 px-2">
          <a href="/how-it-works" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">How it works</a>
          <a href="/features" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">Features</a>
          <a href="/examples" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">Examples</a>
          <a href="/guides" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">Guides</a>
          <a href="/tools" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">Tools</a>
          <a href="/faq" target="_blank" rel="noopener noreferrer" className="text-xs text-warm-grey transition-colors hover:text-red">FAQ</a>
        </div>
      </div>
    </div>
  )
}

/* ── Credits Summary ────────────────────────────────────────────── */

function CreditsSummary() {
  const { displayBalance, percentage, isLow } = useCreditBalance()

  return (
    <div className="rounded-xl border border-stone/30 bg-white p-5">
      <h2 className="mb-3 font-display text-base font-semibold text-charcoal">
        Credits
      </h2>
      <div className="flex items-center gap-4">
        {/* Ring */}
        <div className="relative size-14 shrink-0">
          <svg viewBox="0 0 36 36" className="size-14 -rotate-90">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E8E4DF" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.5" fill="none"
              stroke="#C41E2A"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${percentage * 0.974} 97.4`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums text-charcoal">
            {percentage > 0 ? `${Math.round(percentage)}%` : "0"}
          </span>
        </div>
        <div>
          <p className="text-2xl font-semibold tabular-nums text-charcoal">
            {displayBalance.toLocaleString()}
          </p>
          <p className="text-xs text-warm-grey">credits remaining</p>
        </div>
      </div>
      <div className="mt-4">
        <Button asChild variant="secondary" size="sm" className="w-full">
          <Link href="/dashboard/credits">
            {isLow ? "Top up credits" : "Manage credits"}
          </Link>
        </Button>
      </div>
    </div>
  )
}

/* ── Search Results (preserved) ─────────────────────────────────── */

function SearchResults({
  q,
  listings,
}: {
  q: string
  listings: DashboardListing[]
}) {
  const [listingView, setListingView] = useViewPreference("list")

  return (
    <div className="mt-8">
      <p className="text-sm text-warm-grey">
        Results for <span className="font-medium text-charcoal">&ldquo;{q}&rdquo;</span>
      </p>

      {listings.length === 0 && (
        <div className="mt-12 flex flex-col items-center py-12 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-stone/30">
            <svg className="size-6 text-warm-grey/60" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <p className="mt-4 text-sm text-warm-grey">No results found. Try a different search term.</p>
        </div>
      )}

      {listings.length > 0 && (
        <section className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-charcoal">
              Listings <span className="text-warm-grey/60">({listings.length})</span>
            </h3>
            <ViewToggle view={listingView} onChange={setListingView} />
          </div>
          {listingView === "grid" ? (
            <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((listing) => (
                <ListingCardWithVideos key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {listings.map((listing) => (
                <ListingRowWithVideos key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
